export const ICON_NAMES = [
  'menu',
  'close',
  'home',
  'help',
  'success',
  'warning',
  'error',
  'download'
] as const;

export type IconName = (typeof ICON_NAMES)[number];

export interface ApplicationMetadataConfig {
  title: string;
  language: string;
  ariaLabel: string;
}

export interface LayoutConfig {
  showNavigation: boolean;
}

export interface FeatureFlagsConfig {
  showHeader: boolean;
  showFooter: boolean;
  showHelpButton: boolean;
  showPromotionalBanner: boolean;
  showDownloadButton: boolean;
  showContactSection: boolean;
  showMarketPlaceHeader: boolean;
  // Oculta la tarjeta "¡Hola X, encuentra la oferta ideal..." (buscador de categoría) en la página de ofertas.
  showCategoryFinder: boolean;
}

export interface BrandColorsConfig {
  primary: string;
  onPrimary: string;
  secondary: string;
  onSecondary: string;
  // Color de acento para CTAs puntuales (ej. "Aplicar ahora"), distinto de primary/secondary.
  tertiary: string;
  onTertiary: string;
  background: string;
  surface: string;
  text: string;
  mutedText: string;
  border: string;
  success: string;
  warning: string;
  danger: string;
  // Variantes del sistema de "etiqueta de oferta" (bordes/chips de card y ui-sponsor-badge). El
  // verde de esa etiqueta reutiliza 'tertiary' (mismo hex histórico); blue/orange no calzan con
  // ningún otro token, por eso tienen su propia variable.
  labelBlue: string;
  labelOrange: string;
  // Fondo de la sección de listado de ofertas (.body-market-place). Separado de 'background' para
  // poder cambiarlo de forma independiente sin afectar el fondo general de la app.
  marketPlaceBackground: string;
}

export interface BrandTypographyConfig {
  fontFamily: string;
  headingFontFamily: string;
  baseFontSize: string;
  headingFontWeight: number;
  stylesheetUrl?: string;
}

export interface BrandRadiusConfig {
  small: string;
  medium: string;
  large: string;
  pill: string;
}

export interface BrandSpacingConfig {
  small: string;
  medium: string;
  large: string;
}

export interface BrandThemeConfig {
  colors: BrandColorsConfig;
  typography: BrandTypographyConfig;
  radius: BrandRadiusConfig;
  spacing: BrandSpacingConfig;
}

export interface BrandLogosConfig {
  primary: string;
  compact: string;
  footer: string;
}

export type BrandIconsConfig = Record<IconName, string>;

// Nombres de Material Icons (ligature) por entidad de negocio (ej. 'usuarios', 'roles'), definidos libremente por cada tenant.
export type EntityIconsConfig = Record<string, string>;

export interface BrandAssetsConfig {
  logos: BrandLogosConfig;
  favicon: string;
  icons: BrandIconsConfig;
  entityIcons: EntityIconsConfig;
  // Imagen de fondo opcional de .body-market-place. Si el tenant no la define, no se renderiza
  // ninguna imagen y solo se ve 'marketPlaceBackground' (color sólido).
  marketPlaceBackgroundImage?: string;
}

export interface AppRuntimeConfig {
  schemaVersion: 1;
  tenant: string;
  application: ApplicationMetadataConfig;
  layout: LayoutConfig;
  features: FeatureFlagsConfig;
  theme: BrandThemeConfig;
  assets: BrandAssetsConfig;
}
